import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, Film, ArrowRight, FileText } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { PIANO_VIDEOS } from "@/data/videos";
import { VIDEO_CAPTIONS } from "@/data/videoCaptions";
import { useA11y } from "@/context/A11yContext";

export const PianoVideos = ({ limit, compact = false, instrumentId = "pianists" }) => {
  const [open, setOpen] = useState(null);
  const { lyrics } = useA11y();
  const list = limit ? PIANO_VIDEOS.slice(0, limit) : PIANO_VIDEOS;
  return (
    <section className="mt-8" data-testid={compact ? "piano-videos-highlights" : "piano-videos"}>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Film size={14} /> Jason in action</p>
      <h2 className={`mt-2 font-serif ${compact ? "text-2xl" : "text-3xl"}`}>Jason on keys — live, in church and on stage</h2>
      {!compact && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Watch how reading, harmony and melody come together in real performances. This is where the courses take you.</p>}
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((v) => (
          <button key={v.id} onClick={() => setOpen(v)} className="group overflow-hidden rounded-2xl border border-border bg-card text-left" data-testid={`piano-video-${v.id}`}>
            <div className="relative aspect-video overflow-hidden bg-black">
              <img src={v.poster} alt={v.title} loading="lazy" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-transform group-hover:scale-110"><Play size={22} className="ml-1" /></span>
              </span>
            </div>
            <p className="px-4 py-3 font-serif text-lg">{v.title}</p>
          </button>
        ))}
      </div>
      {compact && (
        <Link to={`/courses?instrument=${instrumentId}&view=videos`} data-testid="see-all-videos-link" className="mt-5 inline-flex items-center gap-2 text-base font-semibold sm:text-lg text-primary hover:underline">
          See all {PIANO_VIDEOS.length} videos <ArrowRight size={18} />
        </Link>
      )}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-md" data-testid="piano-video-modal">
            <button onClick={() => setOpen(null)} aria-label="Close video" className="absolute right-4 top-4 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20" data-testid="piano-video-close"><X size={22} /></button>
            <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <video key={open.id} src={open.src} poster={open.poster} controls autoPlay playsInline crossOrigin="anonymous" aria-label={`${open.title} — live music performance`} className="max-h-[80vh] w-full rounded-2xl bg-black" data-testid="piano-video-player">
                {lyrics && VIDEO_CAPTIONS[open.id] && <track kind="captions" srcLang="en" label="English (auto-generated)" src={`/media/captions/${open.id}.vtt`} default />}
              </video>
              <p className="mt-3 text-center font-serif text-lg text-white">{open.title}</p>
              {!VIDEO_CAPTIONS[open.id] ? (
                <p className="mt-1 text-center text-xs text-white/70" data-testid="piano-video-audio-note">[Live music performance — instrumental / no lyrics]</p>
              ) : lyrics ? (
                <p className="mt-1 text-center text-xs text-white/70" data-testid="piano-video-audio-note">
                  [Live music performance — lyrics are auto-generated and may contain errors]
                  <Link to={`/lyrics/${open.id}`} onClick={() => setOpen(null)} data-testid="piano-video-lyrics-sheet" className="ml-2 inline-flex items-center gap-1 font-semibold text-primary hover:underline"><FileText size={12} /> Lyrics sheet</Link>
                </p>
              ) : (
                <button onClick={() => { setOpen(null); window.dispatchEvent(new Event("jt-open-a11y")); }} data-testid="piano-video-lyrics-hint"
                  className="mx-auto mt-1 block text-xs text-white/60 underline-offset-2 transition-colors hover:text-white hover:underline">
                  Lyrics available — turn on in Accessibility
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
